<script setup lang="ts">
    interface UspBarProps {
        sequence?: UspItemData[];
    }

    const props = defineProps<UspBarProps>();

    const { id } = useInjectWidgetInfo();
    const { resolveUrlLink } = useURL();

    const currentColumn = ref(0);
    const isMaxMd = ref(false);
    useMediaQuery([
        {
            query: defaultQueries.max.md,
            ref: isMaxMd,
        },
    ]);

    const fadeTransition = (index: number) => {
        const classes = [];
        if (index === currentColumn.value) {
            classes.push('max-lg:opacity-100 max-lg:z-10');
        } else if (index > currentColumn.value + 1) {
            classes.push('max-lg:opacity-0 max-lg:delay-100');
        } else {
            classes.push('max-lg:opacity-0');
        }
        return classes.join(' ');
    };

    const uspData = props.sequence
        ?.map((usp) => {
            const { url } = resolveUrlLink(usp.link);
            const title = usp.title;
            const icon = usp.icon?.data?.icon;
            if (!title || !icon) return;

            return {
                title,
                // subtitle not implemented
                icon,
                link: url || '',
                campaignName: usp.link?.campaignName,
            };
        })
        .filter(isDefined);

    watch(
        [isMaxMd, currentColumn],
        async () => {
            if (isMaxMd.value) {
                await wait(4000);
                if (currentColumn.value >= (props.sequence?.length || 0) - 1) {
                    currentColumn.value = 0;
                } else {
                    currentColumn.value = currentColumn.value + 1;
                }
            }
        },
        { deep: true, immediate: true },
    );
</script>

<template>
    <div
        v-if="uspData && uspData.length > 0"
        class="mol-ups-bar bg-woom-white-smoke py-6 md:py-8">
        <atm-container>
            <atm-grid tag="ul">
                <li
                    v-for="(item, index) in uspData"
                    :id="'usp-row-' + index"
                    :key="'usp-row-' + index"
                    :data-index="'usp-row-' + index"
                    :class="[
                        'relative col-span-full max-lg:col-start-1 max-lg:row-start-1 max-lg:opacity-0 max-lg:transition-opacity max-lg:duration-300 max-lg:ease-out',
                        {
                            'lg:col-span-2': uspData.length > 4,
                            'lg:col-span-3': uspData.length === 4,
                            'lg:col-span-4': uspData.length === 3,
                            'lg:col-span-6': uspData.length === 2,
                            'lg:col-span-12': uspData.length === 1,
                        },
                        fadeTransition(index),
                    ]">
                    <nuxt-link
                        v-if="item"
                        :id="arrayToId([item.campaignName, id, `${index}`])"
                        :to="item.link"
                        :class="['flex h-full items-center gap-3 text-xs max-lg:flex-col', { 'transition-colors hover:text-woom-red': item.link }]">
                        <component
                            :is="item.icon"
                            class="h-7 w-7 shrink-0 fill-current" />
                        {{ item.title }}
                    </nuxt-link>
                </li>
            </atm-grid>
        </atm-container>
    </div>
</template>
